import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Shared right-click context menu for editable grid cells — BO UI Standard v2
 * §9 "Editable grids" (ruling 2026-07-02).
 *
 * Two actions:
 *   • **Copy to All Cells**     — apply the clicked cell's value down the whole column.
 *   • **Copy to Selected Rows** — apply it to the checked rows only (hidden when the
 *     grid has no row selection — omit `onCopySelected`).
 *
 * Guards live in the CALLER (per the standard): don't open the menu on an empty
 * value (`canBulkCopy`), and show a warn toast — not an apply — when Copy to
 * Selected is clicked with zero rows selected.  The bulk apply itself is expected
 * to be LOCAL state persisted by the form's existing audited Save (one audit row
 * per bulk copy through that save).
 *
 * Styling: `.cell-ctx-menu` in `theme/boStandard.css` (white, radius 8, shadow,
 * --ai-* tokens).  The menu clamps itself inside the viewport.
 */
export type CellContextMenuProps = {
  /** The clicked cell's value (shown in the menu header). */
  value: unknown;
  /** Column header label, e.g. "Read" / "CollectedBy". */
  columnLabel: string;
  /** How many rows are currently selected (shown on the Selected action). */
  selectedCount?: number;
  /** Apply the value to every row (whole column). */
  onCopyAll: () => void;
  /** Apply the value to the selected rows only.  Omit to hide the action
   *  (grids without a row-selection column offer only Copy to All Cells). */
  onCopySelected?: () => void;
  /** Viewport coordinates of the right-click (e.clientX / e.clientY). */
  position: { x: number; y: number };
  onClose: () => void;
};

/**
 * True when a cell value is bulk-copyable.  Empty string / null / undefined are
 * NOT (the caller shows "Cell is empty." instead of opening the menu); booleans
 * and numbers always are — an unchecked checkbox (`false`) is a real value.
 */
export function canBulkCopy(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

/** Short human preview of the copied value for the menu header. */
function preview(value: unknown): string {
  if (typeof value === 'boolean') return value ? '✓ checked' : '✗ unchecked';
  const s = String(value);
  return s.length > 24 ? `${s.slice(0, 24)}…` : s;
}

export function CellContextMenu({
  value, columnLabel, selectedCount, onCopyAll, onCopySelected, position, onClose,
}: CellContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(position);

  // Clamp inside the viewport (same rule as the column filter popups §18.6).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let { x, y } = position;
    if (x + r.width > window.innerWidth - 8) x = Math.max(8, window.innerWidth - r.width - 8);
    if (y + r.height > window.innerHeight - 8) y = Math.max(8, window.innerHeight - r.height - 8);
    setPos({ x, y });
  }, [position]);

  // Close on outside click / Escape / scroll — standard transient-popup rules.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    // Capture phase + handled: Esc closes ONLY this menu. On the document it ran
    // after the hotkey store, whose Esc = the form's Cancel (closed the form
    // behind — Open Quote, 2026-10-20).
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  return (
    <div ref={ref} className="cell-ctx-menu" role="menu"
      style={{ left: pos.x, top: pos.y }}
      onContextMenu={e => e.preventDefault()}>
      <div className="cell-ctx-head">
        <span className="cell-ctx-col">{columnLabel}</span>
        <span className="cell-ctx-val">{preview(value)}</span>
      </div>
      <button type="button" role="menuitem" className="cell-ctx-item"
        onClick={() => { onCopyAll(); onClose(); }}>
        <i className="ti ti-copy" /> Copy to All Cells
      </button>
      {onCopySelected && (
        <button type="button" role="menuitem" className="cell-ctx-item"
          onClick={() => { onCopySelected(); onClose(); }}>
          <i className="ti ti-copy-check" /> Copy to Selected Rows
          {typeof selectedCount === 'number' && <span className="cell-ctx-count">({selectedCount})</span>}
        </button>
      )}
    </div>
  );
}
